import {
  getApiPermission,
  getPagePermission,
} from '@/config/permissions.config';
import { delocalizePath, isLocale } from '@/lib/i18n-routes';

export type Session = {
  user?: { permissions?: string[] } | null;
} | null;

/**
 * Get all user permissions from session
 */
export function getUserPermissions(session: Session | null): string[] {
  return session?.user?.permissions ?? [];
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(
  session: Session | null,
  permissionSlug: string,
): boolean {
  return (session?.user?.permissions ?? []).includes(permissionSlug);
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
  session: Session | null,
  permissionSlugs: string[],
): boolean {
  const permissions = session?.user?.permissions ?? [];
  return permissionSlugs.some((slug) => permissions.includes(slug));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
  session: Session | null,
  permissionSlugs: string[],
): boolean {
  const permissions = session?.user?.permissions ?? [];
  return permissionSlugs.every((slug) => permissions.includes(slug));
}

/**
 * Check permission for a page route using the token permissions directly.
 * Strips locale prefix if present before checking.
 */
export function checkPagePermission(
  session: Session | null,
  path: string,
): boolean {
  // Strip locale prefix if the path starts with one (e.g. /de/admin -> /admin)
  const segments = path.split('/').filter(Boolean);
  const cleanPath =
    segments.length > 0 && isLocale(segments[0])
      ? delocalizePath(path).pathname
      : path;
  const permission = getPagePermission(cleanPath);
  if (!permission) return true;
  return hasPermission(session, permission);
}

/**
 * Check permission for an API route using the token permissions directly.
 */
export function checkApiPermission(
  session: Session | null,
  path: string,
): boolean {
  const permission = getApiPermission(path);
  if (!permission) return true;
  return hasPermission(session, permission);
}

/**
 * Check if user has admin access (admin.view permission).
 */
export function isAdmin(session: Session | null): boolean {
  return hasPermission(session, 'admin.view');
}

/**
 * Check if user is a guest (not authenticated).
 */
export function isGuest(session: Session | null): boolean {
  return !session?.user;
}
